import { useState, type FormEvent } from 'react';
import { Alert } from '@/components/alert';
import { Button } from '@/components/button';
import { CheckboxField } from '@/components/checkbox-field';
import { LEGAL_URLS } from '@/components/legal-links';
import type { Copy } from '@/lib/messages';

export type LegalDocument = 'terms' | 'privacy';

interface RequireTermsStepProps {
  copy: Copy;
  pending: boolean;
  error: string | null;
  /** Only what is actually outstanding — a republished privacy policy does
   *  not ask someone to re-agree to terms they already accepted. */
  documents: LegalDocument[];
  /**
   * Present only on a re-gated live session — nobody signed in on this screen,
   * so it names whose agreement is about to be recorded, and offers a way out
   * for someone who is not that person (ADR 0037).
   */
  account?: { email: string };
  /** Ends the session so a different person can sign in as themselves. */
  onSignOut?: () => void;
  onSubmit: () => void;
}

/**
 * The mandatory Terms of Use / Privacy Policy step
 * (docs/auth/40-features/terms-acceptance.md).
 *
 * No skip, and no token is issued until both are accepted. Declining is
 * leaving — the flow renders a "cancel and go back to the app" control beneath
 * this step that aborts the authorization (the RP gets access_denied), which
 * writes nothing to the account.
 *
 * Reached two ways. Usually a login was just proven and is held server-side.
 * But a live session whose documents were republished lands here having
 * authenticated nothing on this screen (ADR 0037) — and on a shared browser
 * the person reading it may not be the session's owner. That variant carries
 * `account` and names it: the consent this records is a legal statement about
 * a specific person, and aborting would not help, since the session would just
 * re-gate on the next attempt. Signing out is the escape.
 */
export function RequireTermsStep({
  copy,
  pending,
  error,
  documents,
  account,
  onSignOut,
  onSubmit,
}: RequireTermsStepProps) {
  const [accepted, setAccepted] = useState<Record<LegalDocument, boolean>>({
    terms: false,
    privacy: false,
  });

  // A document that is not outstanding is not shown, and must not gate the
  // button — the server would not record it either.
  const complete = documents.every((document) => accepted[document]);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!complete) return;
    onSubmit();
  }

  function link(document: LegalDocument) {
    return (
      <a
        href={LEGAL_URLS[document]}
        target="_blank"
        rel="noreferrer noopener"
        className="focus-ring rounded underline hover:text-buds-dark"
      >
        {document === 'terms' ? copy.legalTerms : copy.legalPrivacy}
      </a>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      {error ? <Alert tone="error">{error}</Alert> : null}

      {account ? (
        <div className="rounded-xl bg-buds-dark/5 px-4 py-3 text-sm text-buds-muted">
          <p>{copy.termsRegateSignedInAs(account.email)}</p>
          {onSignOut ? (
            <button
              type="button"
              onClick={onSignOut}
              className="focus-ring mt-1 rounded underline hover:text-buds-dark"
            >
              {copy.termsRegateNotYou}
            </button>
          ) : null}
        </div>
      ) : null}

      {documents.includes('terms') ? (
        <CheckboxField
          id="accept-terms"
          checked={accepted.terms}
          onChange={(event) =>
            setAccepted((prev) => ({ ...prev, terms: event.target.checked }))
          }
          label={
            <>
              {copy.termsCheckboxBefore}
              {link('terms')}
              {copy.termsCheckboxAfter}
            </>
          }
        />
      ) : null}

      {documents.includes('privacy') ? (
        <CheckboxField
          id="accept-privacy"
          checked={accepted.privacy}
          onChange={(event) =>
            setAccepted((prev) => ({ ...prev, privacy: event.target.checked }))
          }
          label={
            <>
              {copy.privacyCheckboxBefore}
              {link('privacy')}
              {copy.privacyCheckboxAfter}
            </>
          }
        />
      ) : null}

      <Button type="submit" loading={pending} disabled={pending || !complete}>
        {copy.termsRequiredSubmit}
      </Button>
    </form>
  );
}
